import { GroupRoles, RolesClaim } from '@bp/pim-auth-constants';
import { createContext, ReactNode, useContext, useMemo, useRef } from 'react';
import { FragmentType, getFragmentData, graphql } from '../client/gql';
import { useAuthClaims } from '../hooks/useAuthClaims';
import { BpPermissionChecker } from '../utils/PermissionChecker';
import { OrganizationConfigContext } from './OrganizationConfigContext';

export const PermissionCheckerFieldsDocument = graphql(`
  fragment PermissionCheckerFields on Query {
    profilePermissions: profiles(where: { uuid: $profile }) {
      __typename
      uuid
      nextcloudConnection {
        edges {
          properties {
            enabled
          }
        }
      }
    }
    groupPermission: groups(
      where: {
        OR: [
          { admins_SOME: { uuid: $profile } }
          { editors_SOME: { uuid: $profile } }
          { viewers_SOME: { uuid: $profile } }
        ]
      }
    ) {
      uuid
      admins {
        uuid
      }
      editors {
        uuid
      }
      viewers {
        uuid
      }
    }
  }
`);

type PermissionCheckerProviderProps = {
  query: FragmentType<typeof PermissionCheckerFieldsDocument> | undefined;
  children: ReactNode;
};

export const PermissionCheckerContext = createContext<BpPermissionChecker | null>(null);

export const PermissionCheckerProvider = ({ children, query }: PermissionCheckerProviderProps) => {
  const fragment = getFragmentData(PermissionCheckerFieldsDocument, query);
  const groupPermission = fragment?.groupPermission;
  const profilePermission = fragment?.profilePermissions;
  const { pimAuthClaims } = useAuthClaims();
  const profile = pimAuthClaims.getProfile();
  const roles = pimAuthClaims.getRoles();
  const user = pimAuthClaims.getUser();
  const otherProfiles = pimAuthClaims.getOtherProfiles();
  const childProfiles = pimAuthClaims.getChildProfiles();
  const organization = pimAuthClaims.getOrganization();
  const omniOrganization = pimAuthClaims.getOmniOrganization();

  const permissionChecker = useRef<BpPermissionChecker | null>(null);

  const groupRoles = useMemo(() => {
    const roles: RolesClaim = [];
    groupPermission?.forEach((group) => {
      if (group.admins.find((p) => p.uuid === profile?.uuid)) {
        roles.push({
          target: { type: 'Group', uuid: group.uuid },
          name: GroupRoles.Admin,
        });
      }
      if (group.editors.find((p) => p.uuid === profile?.uuid)) {
        roles.push({
          target: { type: 'Group', uuid: group.uuid },
          name: GroupRoles.Editor,
        });
      }
      if (group.viewers.find((p) => p.uuid === profile?.uuid)) {
        roles.push({
          target: { type: 'Group', uuid: group.uuid },
          name: GroupRoles.Viewer,
        });
      }
    });

    return roles;
  }, [groupPermission, profile]);

  const hasNextcloudUser = useMemo(() => {
    return profilePermission?.some((p) => p.nextcloudConnection.edges.some((ne) => ne.properties.enabled)) ?? false;
  }, [profilePermission]);

  const { allowStudentDirectMessages, allowParentExcuses } = useContext(OrganizationConfigContext);

  if (!profile || !roles || !user || !omniOrganization || !organization) {
    console.warn('Insufficient claims for permission checker. Returning deny-all permission checker');
    // todo BpDenyAllPermissionChecker
    permissionChecker.current = null;
  } else {
    permissionChecker.current = new BpPermissionChecker(
      {
        profile: profile,
        organization: organization,
        roles: [...roles, ...groupRoles],
        user: user,
        omniOrganization: omniOrganization,
        otherProfiles: otherProfiles,
        childProfiles: childProfiles,
      },
      allowStudentDirectMessages,
      allowParentExcuses,
      hasNextcloudUser,
    );
  }

  return (
    <PermissionCheckerContext.Provider value={permissionChecker.current}>{children}</PermissionCheckerContext.Provider>
  );
};
